<template>
  <div class="clubs-section" :class="{ 'rtl': isRTL }">
    <div class="clubs-container">
      <div class="club-card">
        <h2 class="clubs-title">
          <span>{{ t('clubs.heading_part1') }}</span>
          <span class="highlight">{{ t('clubs.heading_highlight') }}</span>
          <span>{{ t('clubs.heading_part2') }}</span>
        </h2>

        <div class="club-header">
          <h3 class="club-name">{{ currentClub.name }}</h3>
          <div class="nav-buttons nav-buttons-desktop">
            <button class="nav-button prev" @click="previousClub" :disabled="currentIndex === 0">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
            <button class="nav-button next" @click="nextClub" :disabled="currentIndex === clubs.length - 1">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </div>
        </div>

        <div class="club-content">
          <div class="image-nav-wrapper">
            <button class="nav-button next nav-button-mobile" @click="nextClub" :disabled="currentIndex === clubs.length - 1">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>

            <div class="club-image">
              <img :src="currentClub.image" :alt="currentClub.name" />
            </div>

            <button class="nav-button prev nav-button-mobile" @click="previousClub" :disabled="currentIndex === 0">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </div>

          <div class="club-info">
            <p class="club-description">{{ currentClub.description }}</p>
          </div>

          <div class="clubs-footer-mobile">
            <p class="footer-text">
              <span>{{ t('clubs.additional_text1') }}</span>
              <span class="highlight">{{ t('clubs.additional_text2') }}</span>
              <span>{{ t('clubs.additional_text3') }}</span>
            </p>

            <CtaButton
              :title="t('clubs.cta_title')"
              :subtitle="t('clubs.cta_subtitle')"
              @click="handleCtaClick"
            />
          </div>
        </div>

        <div class="clubs-footer clubs-footer-desktop">
          <p class="footer-text">
            <span>{{ t('clubs.additional_text1') }}</span>
            <span class="highlight">{{ t('clubs.additional_text2') }}</span>
            <span>{{ t('clubs.additional_text3') }}</span>
          </p>

          <CtaButton
            :title="t('clubs.cta_title')"
            :subtitle="t('clubs.cta_subtitle')"
            @click="handleCtaClick"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useLanguage } from '@/composables/useLanguage'
import CtaButton from '../ui/CtaButton.vue'

const { t, isRTL } = useLanguage()

const props = defineProps({
  clubs: {
    type: Array,
    default: () => [
      {
        id: 1,
        name: 'TALENT CLUB',
        description: 'It will allow your children to express themselves and their hobbies in English, whether through singing, drawing, or other talents.',
        students: 8,
        rating: '9/10',
        tags: ['Interaction', 'Fun', 'Self-confidence', 'Performance', 'Creativity'],
        image: '/assets/talent-club.png' // You'll need to provide this
      }
    ]
  }
})

const emit = defineEmits(['cta-click'])

const currentIndex = ref(0)

const currentClub = computed(() => props.clubs[currentIndex.value])

const nextClub = () => {
  if (currentIndex.value < props.clubs.length - 1) {
    currentIndex.value++
  }
}

const previousClub = () => {
  if (currentIndex.value > 0) {
    currentIndex.value--
  }
}

const handleCtaClick = () => {
  emit('cta-click')
}
</script>

<style scoped>
.clubs-section {
  width: 100%;
  max-width: 1760px;
  margin: 0 auto;
  padding: clamp(40px, 8vw, 80px) clamp(15px, 3vw, 20px);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: clamp(30px, 5vw, 48px);
  box-sizing: border-box;
}

.clubs-title {
  font-size: clamp(24px, 4.5vw, 48px);
  line-height: 1.3;
  font-weight: 700;
  text-transform: uppercase;
  color: #313131;
  margin: 0 0 clamp(20px, 3vw, 32px);
  padding: clamp(10px, 2vw, 20px) clamp(15px, 3vw, 30px) 0;
  opacity: 0.9;
  text-align: center;
}

.highlight {
  color: #f47d21;
}

.clubs-container {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.nav-buttons {
  display: flex;
  gap: 12px;
}

.nav-button-mobile {
  display: none;
}

.image-nav-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
}

.nav-button {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: transparent;
  border: 2px solid #ffffff;
  color: #ffffff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  flex-shrink: 0;
}

.nav-button svg {
  width: 20px;
  height: 20px;
}

.nav-button:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.2);
  transform: scale(1.05);
}

.nav-button:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.club-card {
  flex: 1;
  width: 100%;
  max-width: 100%;
  background: rgba(250, 222, 190, 0.1);
  border: 1px solid #aeaeae;
  border-radius: clamp(30px, 5vw, 50px);
  padding: clamp(15px, 2.5vw, 25px);
  box-shadow: 0px 4px 9.4px rgba(0, 0, 0, 0.02);
  position: relative;
  overflow: hidden;
  box-sizing: border-box;
}

.club-card::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 20%;
  transform: translate(-50%, -50%);
  filter: blur(134px);
  border-radius: 50%;
  background-color: #0095ff;
  width: 238px;
  height: 135px;
  opacity: 0.34;
  pointer-events: none;
  z-index: 0;
}

.club-header {
  background: linear-gradient(90deg, #003d5c 0%, #005580 100%);
  border-radius: clamp(20px, 3vw, 30px);
  padding: clamp(16px, 2.5vw, 20px) clamp(20px, 3vw, 30px);
  margin-bottom: clamp(15px, 2.5vw, 25px);
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  position: relative;
  z-index: 1;
}

.club-name {
  font-size: clamp(22px, 3.5vw, 36px);
  font-weight: 700;
  color: #ffffff;
  margin: 0;
  text-align: left;
  letter-spacing: 1px;
  flex: 1;
  min-width: 0;
}

.club-content {
  display: grid;
  grid-template-columns: 0.5fr 1fr;
  align-items: center;
  position: relative;
  z-index: 1;
  width: 100%;
}

.club-image {
  width: 100%;
  max-width: 450px;
  aspect-ratio: 1;
  border-radius: clamp(20px, 3vw, 30px);
  overflow: hidden;
  background: linear-gradient(135deg, #0095ff 0%, #005580 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  margin: 0;
}

.club-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.club-info {
  display: flex;
  flex-direction: column;
  gap: clamp(16px, 3vw, 24px);
  padding: clamp(10px, 2vw, 20px) 0;
  text-align: start;
  align-items: flex-end;
  justify-content: center;
  height: 100%;
}

html[lang="en"] .club-info {
  align-items: flex-start;
}

.club-description {
  font-size: clamp(20px, 2.5vw, 28px);
  line-height: 1.6;
  color: #313131;
  margin: 0;
  text-align: start;
  font-weight: 500;
}

.clubs-footer {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: clamp(24px, 4vw, 32px);
  padding: clamp(20px, 4vw, 40px) clamp(10px, 2vw, 20px);
  position: relative;
  z-index: 1;
}

.clubs-footer-mobile {
  display: none;
}

.footer-text {
  font-size: clamp(20px, 3.5vw, 36px);
  line-height: 1.4;
  text-align: center;
  color: #313131;
  margin: 0;
  font-weight: 500;
  letter-spacing: -0.02em;
  text-transform: uppercase;
  opacity: 0.9;
  width: 100%;
}

@media (max-width: 1024px) {
  .clubs-section {
    padding: clamp(30px, 6vw, 60px) clamp(15px, 3vw, 20px);
  }

  .club-content {
    grid-template-columns: 1fr;
    gap: clamp(10px, 2vw, 15px);
  }

  .club-image {
    max-width: 450px;
    width: 100%;
    margin: 0 auto;
  }
}

@media (max-width: 768px) {
  .clubs-section {
    padding: clamp(20px, 4vw, 24px) clamp(20px, 5vw, 44px);
    gap: clamp(35px, 7vw, 48px);
  }

  .nav-buttons-desktop {
    display: none;
  }

  .nav-button-mobile {
    display: flex;
  }

  .club-card {
    max-width: 363px;
    width: 100%;
    border-radius: 18.67px;
    border: 0.4px solid #b6b6b6;
    box-shadow: 0px 1.49px 3.51px rgba(0, 0, 0, 0.02);
    padding: 0;
    overflow: visible;
  }

  .club-header {
    border-radius: 20px;
    padding: clamp(16px, 3vw, 20px) clamp(25px, 5vw, 33px);
    margin: 0;
    order: 1;
    border: 1px solid #fff;
  }

  .club-name {
    text-align: center;
    width: 100%;
    font-size: clamp(20px, 4vw, 24px);
    letter-spacing: 0.17px;
  }

  .club-content {
    display: flex;
    flex-direction: column;
    gap: 0;
    padding: 0;
  }

  .club-info {
    order: 1;
    padding: clamp(15px, 3vw, 19px) clamp(6px, 1.2vw, 7px) clamp(8px, 1.5vw, 10px);
    gap: clamp(8px, 1.5vw, 9.4px);
    text-align: start;
    align-items: flex-end;
  }

  html[lang="en"] .club-info {
    align-items: flex-start;
  }

  .club-description {
    font-size: clamp(18px, 3.5vw, 20px);
    line-height: 130%;
    font-weight: 500;
    color: #1d1d1d;
    text-align: start;
  }

  .image-nav-wrapper {
    order: 2;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: clamp(8px, 2vw, 15px);
    margin-top: clamp(20px, 4vw, 28px);
    width: 100%;
  }

  .club-image {
    max-width: 100%;
    width: calc(100% - 13px);
    margin: 0;
    border-radius: clamp(30px, 6vw, 36px);
    aspect-ratio: 349/241;
  }

  .nav-button-mobile {
    width: clamp(44px, 9vw, 50px);
    height: clamp(44px, 9vw, 50px);
    background: #ffffff;
    color: #003d5c;
    border: 2px solid #ffffff;
  }

  .nav-button-mobile:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.9);
  }

  .clubs-footer-desktop {
    display: none;
  }

  .clubs-footer-mobile {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: clamp(16px, 3vw, 20px);
    padding: clamp(16px, 3vw, 20px) clamp(6px, 1.2vw, 7px);
    order: 3;
    width: 100%;
    max-width: 100%;
  }

  .footer-text {
    font-size: clamp(16px, 3vw, 18px);
    line-height: 1.3;
    letter-spacing: 0.06px;
    text-align: center;
    width: 100%;
    max-width: 343px;
  }
}

@media (max-width: 640px) {
  .clubs-section {
    padding: clamp(20px, 4vw, 24px) clamp(16px, 3vw, 20px);
  }
}

@media (max-width: 480px) {
  .clubs-section {
    padding: clamp(18px, 3.5vw, 24px) clamp(14px, 2.8vw, 16px);
  }

  .club-card {
    max-width: 100%;
  }

  .club-header {
    padding: 14px 20px;
  }

  .nav-buttons {
    max-width: 100%;
    gap: clamp(80px, 25vw, 220px);
  }

  .footer-text {
    font-size: clamp(14px, 3vw, 16px);
    max-width: 100%;
  }

  .club-info {
    padding: 12px 5px 8px;
  }
}

@media (max-width: 380px) {
  .clubs-section {
    padding: 16px 10px;
  }

  .club-card {
    border-radius: 16px;
  }

  .club-header {
    padding: 12px 16px;
    border-radius: 16px;
  }

  .club-name {
    font-size: 18px;
  }

  .club-description {
    font-size: 16px;
    font-weight: 500;
    text-align: start;
  }

  .footer-text {
    font-size: 13px;
  }

  .nav-button-mobile {
    width: 40px;
    height: 40px;
  }
}

/* RTL Support for Arabic */
.clubs-section.rtl .clubs-title {
  text-align: center;
  direction: rtl;
}

.clubs-section.rtl .club-header {
  direction: rtl;
}

.clubs-section.rtl .club-name {
  text-align: right;
  direction: rtl;
}

.clubs-section.rtl .club-info {
  text-align: right;
  direction: rtl;
  align-items: flex-start;
}

.clubs-section.rtl .club-description {
  text-align: right;
  direction: rtl;
}

.clubs-section.rtl .footer-text {
  text-align: center;
  direction: rtl;
}

.clubs-section.rtl .nav-buttons {
  flex-direction: row-reverse;
}
</style>
